import type {
  Candidate, Job, Application, PaginatedResponse, PipelineStage, Tag, CandidateTag,
  Notification, NotificationPreferences, Email, EmailTemplate, CandidateSubmission, Placement, Invoice,
  Interview, JobBoardAccount, JobBoardPosting,
  PipelineFunnelData, RecruiterPerformance, TimeToHireData, SourceAnalysisData,
} from '@/types';
import { normalizeOptionalUrl } from '@/lib/url';
import { ApiRequestError } from './api-error';

const API_BASE = '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    requestId?: string;
    details?: Array<{ field: string; message: string }>;
  };
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private onUnauthorized?: () => void;

  /**
   * Single in-flight refresh promise shared across concurrent 401s.
   * When 5 polled queries simultaneously get 401 (token expired), they all
   * await this one promise instead of each firing their own /auth/refresh —
   * otherwise the second+ calls use an already-consumed refresh token and
   * the user gets kicked out for no reason.
   */
  private refreshInFlight: Promise<boolean> | null = null;

  constructor() {
    this.accessToken = localStorage.getItem('crm_access_token');
    this.refreshToken = localStorage.getItem('crm_refresh_token');
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('crm_access_token', accessToken);
    localStorage.setItem('crm_refresh_token', refreshToken);
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('crm_access_token');
    localStorage.removeItem('crm_refresh_token');
  }

  setOnUnauthorized(callback: () => void) {
    this.onUnauthorized = callback;
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) return false;

    // Coalesce concurrent refresh requests — all 401-hit callers await the
    // same promise, so only one /auth/refresh call is in flight at a time.
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
        });

        // Caddy 502/504 returns HTML, not JSON — don't try to .json() it.
        if (!response.ok) return false;
        const contentType = response.headers.get('content-type') ?? '';
        if (!contentType.includes('application/json')) return false;

        const result = await response.json();
        if (result.success && result.data) {
          this.setTokens(result.data.accessToken, result.data.refreshToken);
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        // Clear the in-flight marker so the next 401 can retry.
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  /**
   * Build the single typed error every API path throws. Reads the correlation id
   * from the X-Request-ID response header (falling back to the error body) so the
   * user-facing message can include a support reference.
   */
  private buildError(
    response: Response,
    body: { error?: { code?: string; message?: string; requestId?: string } } | null,
  ): ApiRequestError {
    return new ApiRequestError({
      message: body?.error?.message || 'Request failed',
      code: body?.error?.code,
      status: response.status,
      requestId: response.headers.get('X-Request-ID') || body?.error?.requestId || undefined,
    });
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle 401 - try refresh token
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        // Retry the original request
        (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
        const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
          ...options,
          headers,
        });
        const retryResult: ApiResponse<T> = await retryResponse.json();
        if (!retryResult.success) {
          throw this.buildError(retryResponse, retryResult);
        }
        return retryResult.data as T;
      } else {
        this.clearTokens();
        this.onUnauthorized?.();
        throw new ApiRequestError({
          message: 'Session expired. Please log in again.',
          code: 'SESSION_EXPIRED',
          status: 401,
        });
      }
    }

    const result: ApiResponse<T> = await response.json();

    if (!result.success) {
      throw this.buildError(response, result);
    }

    return result.data as T;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * Authenticated binary fetch for downloads/exports. Goes through the same auth +
   * 401-refresh as request(), and on failure parses the JSON error envelope and
   * throws the same ApiRequestError — so a download failure surfaces a friendly
   * toast instead of dumping raw JSON into a new browser tab.
   */
  async requestBlob(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<{ blob: Blob; fileName?: string; contentType?: string }> {
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> | undefined),
    };
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });

    // Any 401 ends the session here (refresh if we can, otherwise redirect to
    // login) — never fall through to a silent dead-click on a download/export.
    if (response.status === 401) {
      const refreshed = this.refreshToken ? await this.refreshAccessToken() : false;
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
      } else {
        this.clearTokens();
        this.onUnauthorized?.();
        throw new ApiRequestError({
          message: 'Session expired. Please log in again.',
          code: 'SESSION_EXPIRED',
          status: 401,
        });
      }
    }

    if (!response.ok) {
      // Error responses are JSON envelopes even on a binary endpoint.
      let body: { error?: { code?: string; message?: string; requestId?: string } } | null = null;
      try {
        body = await response.json();
      } catch {
        // Non-JSON error body (e.g. a proxy 502 HTML page) — fall through.
      }
      throw this.buildError(response, body);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition') || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
    const fileName = match ? decodeURIComponent(match[1]) : undefined;
    return { blob, fileName, contentType: response.headers.get('Content-Type') || undefined };
  }

  // NOTE: there is deliberately no presigned-URL helper here. Production MinIO
  // presigns against the internal hostname `opus-minio`, which browsers cannot
  // resolve — file viewing/downloading must always go through the
  // authenticated /files/:id stream (requestBlob). See lib/file-actions.ts.

  // For paginated responses where we need both data and pagination.
  // Any extra envelope fields the backend includes (e.g. completedCount,
  // inProgressCount) are spread onto the returned object so typed callers
  // can pick them up without a second request.
  async getPaginated<T, TExtra extends Record<string, unknown> = Record<string, never>>(
    endpoint: string,
  ): Promise<
    {
      data: T[];
      pagination: { page: number; limit: number; total: number; totalPages: number; hasMore?: boolean };
    } & TExtra
  > {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'GET',
      headers,
    });

    // Handle 401 - try refresh token
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
        const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
          method: 'GET',
          headers,
        });
        const retryResult = await retryResponse.json();
        if (!retryResult.success) {
          throw this.buildError(retryResponse, retryResult);
        }
        const { success: _s, error: _e, data: retryData, pagination: retryPagination, ...retryExtras } = retryResult;
        void _s; void _e;
        return { data: retryData, pagination: retryPagination, ...retryExtras } as {
          data: T[];
          pagination: { page: number; limit: number; total: number; totalPages: number; hasMore?: boolean };
        } & TExtra;
      } else {
        this.clearTokens();
        this.onUnauthorized?.();
        throw new ApiRequestError({
          message: 'Session expired. Please log in again.',
          code: 'SESSION_EXPIRED',
          status: 401,
        });
      }
    }

    const result = await response.json();

    if (!result.success) {
      throw this.buildError(response, result);
    }

    const { success: _s, error: _e, data: resultData, pagination: resultPagination, ...extras } = result;
    void _s; void _e;
    return { data: resultData, pagination: resultPagination, ...extras } as {
      data: T[];
      pagination: { page: number; limit: number; total: number; totalPages: number; hasMore?: boolean };
    } & TExtra;
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient();

// Auth API
export interface LoginResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    avatarUrl: string | null;
    organization: {
      id: string;
      name: string;
      slug: string;
    };
  };
  accessToken: string;
  refreshToken: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatarUrl: string | null;
  phone: string | null;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  lastLoginAt: string | null;
  createdAt: string;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await api.post<LoginResponse>('/auth/login', { email, password });
  api.setTokens(response.accessToken, response.refreshToken);
  return response;
}

export async function getCurrentUser(): Promise<User> {
  return api.get<User>('/auth/me');
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } finally {
    api.clearTokens();
  }
}

// Candidates API
export type CandidateListStatus = 'completed' | 'in_progress' | 'all';

export interface CandidateFilters {
  search?: string;
  skills?: string[];
  location?: string;
  availability?: string;
  onboardingStatus?: string;
  tagId?: string;
  status?: CandidateListStatus;
  page?: number;
  limit?: number;
}

export interface CandidateListResponse extends PaginatedResponse<Candidate> {
  completedCount: number;
  inProgressCount: number;
  totalCount: number;
}

export async function fetchCandidates(
  filters: CandidateFilters = {},
): Promise<CandidateListResponse> {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.skills?.length) params.append('skills', filters.skills.join(','));
  if (filters.location) params.append('location', filters.location);
  if (filters.availability) params.append('availability', filters.availability);
  if (filters.onboardingStatus) params.append('onboarding_status', filters.onboardingStatus);
  if (filters.tagId) params.append('tag_id', filters.tagId);
  // status defaults to 'completed' server-side; only append when caller explicitly sets it.
  if (filters.status) params.append('status', filters.status);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  const queryString = params.toString();
  const endpoint = `/candidates${queryString ? `?${queryString}` : ''}`;

  const response = await api.getPaginated<
    Candidate,
    { completedCount?: number; inProgressCount?: number; totalCount?: number }
  >(endpoint);

  return {
    data: response.data,
    pagination: response.pagination,
    completedCount: response.completedCount ?? response.pagination.total,
    inProgressCount: response.inProgressCount ?? 0,
    totalCount: response.totalCount ?? response.pagination.total,
  };
}

export async function fetchCandidate(id: string): Promise<Candidate> {
  return api.get<Candidate>(`/candidates/${id}`);
}

export interface CreateCandidateInput {
  firstName: string;
  lastName: string;
  email?: string;
  whatsappPhone: string;
  phoneSecondary?: string;
  currentLocation?: string;
  nationality?: string;
  yearsExperience?: number;
  currentJobTitle?: string;
  currentEmployer?: string;
  skills?: string[];
  preferredJobTypes?: string[];
  expectedSalaryRange?: string;
  availabilityDate?: string;
  linkedinUrl?: string;
  source?: string;
}

export async function createCandidate(data: CreateCandidateInput): Promise<Candidate> {
  return api.post<Candidate>('/candidates', {
    first_name: data.firstName,
    last_name: data.lastName,
    email: data.email,
    whatsapp_phone: data.whatsappPhone,
    phone_secondary: data.phoneSecondary,
    current_location: data.currentLocation,
    nationality: data.nationality,
    years_experience: data.yearsExperience,
    current_job_title: data.currentJobTitle,
    current_employer: data.currentEmployer,
    skills: data.skills,
    preferred_job_types: data.preferredJobTypes,
    expected_salary_range: data.expectedSalaryRange,
    availability_date: data.availabilityDate,
    linkedin_url: normalizeOptionalUrl(data.linkedinUrl),
    source: data.source,
  });
}

export interface UpdateCandidateInput extends Partial<CreateCandidateInput> {
  onboardingStatus?: string;
}

export async function updateCandidate(
  id: string,
  data: UpdateCandidateInput,
): Promise<Candidate> {
  const mapped: Record<string, unknown> = {};
  if (data.firstName !== undefined) mapped.first_name = data.firstName;
  if (data.lastName !== undefined) mapped.last_name = data.lastName;
  if (data.email !== undefined) mapped.email = data.email;
  if (data.whatsappPhone !== undefined) mapped.whatsapp_phone = data.whatsappPhone;
  if (data.phoneSecondary !== undefined) mapped.phone_secondary = data.phoneSecondary;
  if (data.currentLocation !== undefined) mapped.current_location = data.currentLocation;
  if (data.nationality !== undefined) mapped.nationality = data.nationality;
  if (data.yearsExperience !== undefined) mapped.years_experience = data.yearsExperience;
  if (data.currentJobTitle !== undefined) mapped.current_job_title = data.currentJobTitle;
  if (data.currentEmployer !== undefined) mapped.current_employer = data.currentEmployer;
  if (data.skills !== undefined) mapped.skills = data.skills;
  if (data.preferredJobTypes !== undefined) mapped.preferred_job_types = data.preferredJobTypes;
  if (data.expectedSalaryRange !== undefined) mapped.expected_salary_range = data.expectedSalaryRange;
  if (data.availabilityDate !== undefined) mapped.availability_date = data.availabilityDate;
  if (data.linkedinUrl !== undefined) mapped.linkedin_url = normalizeOptionalUrl(data.linkedinUrl);
  if (data.source !== undefined) mapped.source = data.source;
  if (data.onboardingStatus !== undefined) mapped.onboarding_status = data.onboardingStatus;
  return api.patch<Candidate>(`/candidates/${id}`, mapped);
}

// Companies API
export interface Company {
  id: string;
  name: string;
  industry: string | null;
  logoUrl: string | null;
}

export interface CompanyListItem {
  id: string;
  name: string;
  industry: string | null;
  website: string | null;
  logoUrl: string | null;
  address: string | null;
  phone: string | null;
  vatNumber: string | null;
  notes: string | null;
  isActive: boolean;
  jobCount: number;
  contactCount: number;
  applicationCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyDetail extends CompanyListItem {
  contacts: CompanyContact[];
  jobs: CompanyJob[];
}

export interface CompanyContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  jobTitle: string | null;
  isPrimary: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyJob {
  id: string;
  title: string;
  status: string;
  applicationCount: number;
}

export interface CompanyFilters {
  search?: string;
  industry?: string;
  page?: number;
  limit?: number;
}

export interface CreateCompanyInput {
  name: string;
  industry?: string | null;
  website?: string | null;
  address?: string | null;
  phone?: string | null;
  vatNumber?: string | null;
  notes?: string | null;
}

export interface UpdateCompanyInput extends Partial<CreateCompanyInput> {}

export interface CreateContactInput {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
  isPrimary?: boolean;
  notes?: string | null;
}

export interface UpdateContactInput extends Partial<CreateContactInput> {}

// Simple list for dropdowns (backward compatible)
export async function fetchCompanies(search?: string): Promise<Company[]> {
  const params = new URLSearchParams();
  params.append('all', 'true');
  if (search) params.append('search', search);
  return api.get<Company[]>(`/companies?${params.toString()}`);
}

// Paginated list for CompaniesPage
export async function fetchCompaniesPaginated(
  filters: CompanyFilters = {},
): Promise<PaginatedResponse<CompanyListItem>> {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.industry) params.append('industry', filters.industry);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));
  const queryString = params.toString();
  const endpoint = `/companies${queryString ? `?${queryString}` : ''}`;
  return api.getPaginated<CompanyListItem>(endpoint);
}

export async function fetchCompany(id: string): Promise<CompanyDetail> {
  return api.get<CompanyDetail>(`/companies/${id}`);
}

export async function createCompany(data: CreateCompanyInput): Promise<CompanyListItem> {
  return api.post<CompanyListItem>('/companies', data);
}

export async function updateCompany(id: string, data: UpdateCompanyInput): Promise<CompanyListItem> {
  return api.patch<CompanyListItem>(`/companies/${id}`, data);
}

export async function deleteCompany(id: string): Promise<void> {
  return api.delete<void>(`/companies/${id}`);
}

export async function createContact(companyId: string, data: CreateContactInput): Promise<CompanyContact> {
  return api.post<CompanyContact>(`/companies/${companyId}/contacts`, data);
}

export async function updateContact(companyId: string, contactId: string, data: UpdateContactInput): Promise<CompanyContact> {
  return api.patch<CompanyContact>(`/companies/${companyId}/contacts/${contactId}`, data);
}

export async function deleteContact(companyId: string, contactId: string): Promise<void> {
  return api.delete<void>(`/companies/${companyId}/contacts/${contactId}`);
}

// Jobs API
export interface JobFilters {
  search?: string;
  status?: string;
  jobType?: string;
  priority?: string;
  page?: number;
  limit?: number;
}

export async function fetchJobs(
  filters: JobFilters = {},
): Promise<PaginatedResponse<Job>> {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.jobType) params.append('job_type', filters.jobType);
  if (filters.priority) params.append('priority', filters.priority);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  const queryString = params.toString();
  const endpoint = `/jobs${queryString ? `?${queryString}` : ''}`;

  return api.getPaginated<Job>(endpoint);
}

export async function fetchJob(id: string): Promise<Job> {
  return api.get<Job>(`/jobs/${id}`);
}

export interface CreateJobInput {
  title: string;
  description?: string;
  requirements?: string;
  location?: string;
  jobType?: string;
  remoteType?: string;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency?: string;
  salaryFrequency?: string;
  status?: string;
  priority?: string;
  positionsAvailable?: number;
  deadline?: string;
  requiredSkills?: string[];
  experienceMin?: number;
  experienceMax?: number;
  companyId?: string;
  candidateSafeTitle?: string;
  candidateSafeDescription?: string;
}

export async function createJob(data: CreateJobInput): Promise<Job> {
  return api.post<Job>('/jobs', {
    title: data.title,
    description: data.description,
    requirements: data.requirements,
    location: data.location,
    job_type: data.jobType,
    remote_type: data.remoteType,
    salary_min: data.salaryMin,
    salary_max: data.salaryMax,
    salary_currency: data.salaryCurrency,
    salary_frequency: data.salaryFrequency,
    status: data.status,
    priority: data.priority,
    positions_available: data.positionsAvailable,
    deadline: data.deadline,
    required_skills: data.requiredSkills,
    experience_min: data.experienceMin,
    experience_max: data.experienceMax,
    company_id: data.companyId,
    candidate_safe_title: data.candidateSafeTitle,
    candidate_safe_description: data.candidateSafeDescription,
  });
}

export interface UpdateJobInput extends Partial<CreateJobInput> {}

export async function updateJob(id: string, data: UpdateJobInput): Promise<Job> {
  const mapped: Record<string, unknown> = {};
  if (data.title !== undefined) mapped.title = data.title;
  if (data.description !== undefined) mapped.description = data.description;
  if (data.requirements !== undefined) mapped.requirements = data.requirements;
  if (data.location !== undefined) mapped.location = data.location;
  if (data.jobType !== undefined) mapped.job_type = data.jobType;
  if (data.remoteType !== undefined) mapped.remote_type = data.remoteType;
  if (data.salaryMin !== undefined) mapped.salary_min = data.salaryMin;
  if (data.salaryMax !== undefined) mapped.salary_max = data.salaryMax;
  if (data.salaryCurrency !== undefined) mapped.salary_currency = data.salaryCurrency;
  if (data.salaryFrequency !== undefined) mapped.salary_frequency = data.salaryFrequency;
  if (data.status !== undefined) mapped.status = data.status;
  if (data.priority !== undefined) mapped.priority = data.priority;
  if (data.positionsAvailable !== undefined) mapped.positions_available = data.positionsAvailable;
  if (data.deadline !== undefined) mapped.deadline = data.deadline;
  if (data.requiredSkills !== undefined) mapped.required_skills = data.requiredSkills;
  if (data.experienceMin !== undefined) mapped.experience_min = data.experienceMin;
  if (data.experienceMax !== undefined) mapped.experience_max = data.experienceMax;
  if (data.companyId !== undefined) mapped.company_id = data.companyId;
  if (data.candidateSafeTitle !== undefined) mapped.candidate_safe_title = data.candidateSafeTitle;
  if (data.candidateSafeDescription !== undefined)
    mapped.candidate_safe_description = data.candidateSafeDescription;
  return api.put<Job>(`/jobs/${id}`, mapped);
}

export async function deleteJob(id: string): Promise<void> {
  return api.delete<void>(`/jobs/${id}`);
}

// Job Applications API
export interface JobApplicationFilters {
  page?: number;
  limit?: number;
}

export async function fetchJobApplications(
  jobId: string,
  filters: JobApplicationFilters = {},
): Promise<PaginatedResponse<Application>> {
  const params = new URLSearchParams();

  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  const queryString = params.toString();
  const endpoint = `/jobs/${jobId}/applications${queryString ? `?${queryString}` : ''}`;

  return api.getPaginated<Application>(endpoint);
}

// Pipeline API
export interface PipelineApplication extends Application {
  candidate: Candidate;
  daysInStage: number;
  lastActivity: string | null;
}

export interface PipelineData {
  jobId: string;
  jobTitle: string;
  stages: Record<string, PipelineApplication[]>;
  totalApplications: number;
}

// Raw API response for job pipeline
interface JobPipelineApiResponse {
  job: {
    id: string;
    title: string;
    status: string;
    company: { id: string; name: string; logoUrl: string | null } | null;
  };
  stages: string[];
  pipeline: Record<string, Array<{
    id: string;
    candidateId: string;
    currentStage: string;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
    lastActivity: string | null;
    candidate: {
      id: string;
      firstName: string;
      lastName: string;
      email: string | null;
      whatsappPhone: string;
      currentLocation: string | null;
      skills: string[];
      yearsExperience: number | null;
      currentJobTitle: string | null;
    };
  }>>;
  totalCount: number;
  stageCounts: Record<string, number>;
}

export async function fetchJobPipeline(jobId: string): Promise<PipelineData> {
  const response = await api.get<JobPipelineApiResponse>(`/jobs/${jobId}/pipeline`);

  // Transform API response to frontend PipelineData format
  const stages: Record<string, PipelineApplication[]> = {};

  for (const [stageName, applications] of Object.entries(response.pipeline)) {
    stages[stageName] = applications.map((app): PipelineApplication => {
      // Calculate days in stage
      const updatedAt = new Date(app.updatedAt);
      const now = new Date();
      const daysInStage = Math.floor((now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24));

      // Build candidate object with all required fields
      const candidate: Candidate = {
        id: app.candidate.id,
        firstName: app.candidate.firstName,
        lastName: app.candidate.lastName,
        email: app.candidate.email,
        whatsappPhone: app.candidate.whatsappPhone,
        phoneSecondary: null,
        currentLocation: app.candidate.currentLocation,
        nationality: null,
        yearsExperience: app.candidate.yearsExperience,
        currentJobTitle: app.candidate.currentJobTitle,
        currentEmployer: null,
        skills: app.candidate.skills || [],
        preferredJobTypes: [],
        expectedSalaryRange: null,
        availabilityDate: null,
        linkedinUrl: null,
        source: 'unknown',
        onboardingStatus: 'unknown',
        createdAt: app.createdAt,
        updatedAt: app.updatedAt,
      };

      return {
        id: app.id,
        jobId: jobId,
        candidateId: app.candidateId,
        currentStage: app.currentStage as PipelineStage,
        disqualificationReason: null,
        notes: app.notes,
        createdAt: app.createdAt,
        updatedAt: app.updatedAt,
        candidate,
        daysInStage,
        lastActivity: app.lastActivity || null,
      };
    });
  }

  return {
    jobId: response.job.id,
    jobTitle: response.job.title,
    stages,
    totalApplications: response.totalCount,
  };
}

export interface UpdateApplicationStageInput {
  stage: string;
  notes?: string;
  disqualificationReason?: string;
}

// Top candidate matching
export interface TopCandidate {
  candidateId: string;
  firstName: string | null;
  lastName: string | null;
  currentJobTitle: string | null;
  currentLocation: string | null;
  yearsExperience: number | null;
  skills: string[];
  matchedSkills: string[];
  matchScore: number;
  breakdown: {
    skills: number;
    titleFit: number;
    experience: number;
    location: number;
    availability: number;
    recency: number;
  };
  reasons: string[];
  aiRank?: number;
  aiRationale?: string;
}

export type TopCandidatesWarning = 'NEEDS_SKILLS' | 'NO_MATCHES';

export interface TopCandidatesResult {
  jobId: string;
  jobTitle: string;
  tier: 'deterministic' | 'ai_reranked';
  fromCache?: boolean;
  aiRerankFailed: boolean;
  errorMessage?: string;
  candidates: TopCandidate[];
  warning?: TopCandidatesWarning;
}

export async function fetchTopCandidates(
  jobId: string,
  options: { limit?: number; rerank?: boolean } = {},
): Promise<TopCandidatesResult> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.rerank) params.set('rerank', 'true');
  const qs = params.toString();
  const path = `/jobs/${jobId}/top-candidates${qs ? `?${qs}` : ''}`;
  return api.get<TopCandidatesResult>(path);
}

export async function updateApplicationStage(
  applicationId: string,
  data: UpdateApplicationStageInput,
): Promise<Application> {
  return api.patch<Application>(`/applications/${applicationId}/stage`, {
    stage: data.stage,
    notes: data.notes,
    disqualification_reason: data.disqualificationReason,
  });
}

export interface CreateApplicationInput {
  jobId: string;
  candidateId: string;
  stage?: string;
  notes?: string;
}

export async function createApplication(data: CreateApplicationInput): Promise<Application> {
  return api.post<Application>('/applications', {
    job_id: data.jobId,
    candidate_id: data.candidateId,
    current_stage: data.stage,
    notes: data.notes,
  });
}

// Candidate Applications API
export async function fetchCandidateApplications(
  candidateId: string,
): Promise<PaginatedResponse<Application>> {
  return api.getPaginated<Application>(`/applications?candidate_id=${candidateId}&limit=100`);
}

// Dashboard API
export interface DashboardStats {
  totalCandidates: number;
  openJobs: number;
  activePipeline: number;
  hiredThisMonth: number;
}

// Raw API response structure (nested)
interface DashboardStatsApiResponse {
  candidates: {
    total: number;
    newThisWeek: number;
    newThisMonth: number;
  };
  jobs: {
    total: number;
    open: number;
    filled: number;
  };
  pipeline: {
    counts: Record<string, number>;
    active: number;
    hiredThisMonth: number;
  };
}

export interface PipelineStageCount {
  stage: PipelineStage;
  count: number;
}

export interface DashboardActivityItem {
  id: string;
  type: 'stage_change' | 'application_created' | 'candidate_created' | 'job_created' | 'note_added';
  description: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  };
  entity?: {
    type: 'candidate' | 'job' | 'application';
    id: string;
    name: string;
  };
  createdAt: string;
}

export interface DashboardAttentionItem {
  id: string;
  candidateId: string;
  candidateName: string;
  jobId: string;
  jobTitle: string;
  currentStage: string;
  daysStuck: number;
}

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const response = await api.get<DashboardStatsApiResponse>('/dashboard/stats');
  // Transform nested API response to flat structure expected by components
  return {
    totalCandidates: response.candidates.total,
    openJobs: response.jobs.open,
    activePipeline: response.pipeline.active,
    hiredThisMonth: response.pipeline.hiredThisMonth,
  };
}

export async function fetchDashboardPipeline(): Promise<PipelineStageCount[]> {
  return api.get<PipelineStageCount[]>('/dashboard/pipeline');
}

export async function fetchDashboardActivity(limit = 20): Promise<DashboardActivityItem[]> {
  // Backend returns a different shape than what the frontend components expect
  // Transform: { action, actionDescription, entityType, entityId, user } → { type, description, user, entity }
  const raw = await api.get<Array<{
    id: string;
    action: string;
    actionDescription: string;
    entityType: string | null;
    entityId: string | null;
    metadata: Record<string, unknown> | null;
    createdAt: string;
    user: { id: string; firstName: string; lastName: string; email: string };
  }>>(`/dashboard/activity?limit=${limit}`);

  return raw.map((item) => {
    // Map backend action to frontend type
    const typeMap: Record<string, DashboardActivityItem['type']> = {
      'application.stage_changed': 'stage_change',
      'application.created': 'application_created',
      'candidate.created': 'candidate_created',
      'job.created': 'job_created',
      'candidate.note_added': 'note_added',
    };

    // Build entity from entityType + entityId + metadata
    let entity: DashboardActivityItem['entity'] | undefined;
    if (item.entityType && item.entityId) {
      const entityType = item.entityType === 'application' ? 'application'
        : item.entityType === 'job' ? 'job'
        : 'candidate';
      const meta = item.metadata as Record<string, string> | null;
      const name = meta?.name || meta?.title || meta?.candidateName || meta?.jobTitle || '';
      entity = { type: entityType as 'candidate' | 'job' | 'application', id: item.entityId, name };
    }

    return {
      id: item.id,
      type: typeMap[item.action] || 'candidate_created',
      description: item.actionDescription,
      user: {
        id: item.user.id,
        firstName: item.user.firstName,
        lastName: item.user.lastName,
        avatarUrl: null,
      },
      entity,
      createdAt: item.createdAt,
    };
  });
}

export async function fetchDashboardAttention(minDays = 7): Promise<DashboardAttentionItem[]> {
  // Backend returns nested shape: { candidate: { id, firstName, lastName }, job: { id, title }, daysSinceUpdate }
  // Frontend expects flat shape: { candidateId, candidateName, jobId, jobTitle, daysStuck }
  const raw = await api.get<Array<{
    id: string;
    currentStage: string;
    daysSinceUpdate: number;
    candidate: { id: string; firstName: string; lastName: string };
    job: { id: string; title: string };
  }>>(`/dashboard/attention?minDays=${minDays}`);

  return raw.map((item) => ({
    id: item.id,
    candidateId: item.candidate.id,
    candidateName: `${item.candidate.firstName} ${item.candidate.lastName}`,
    jobId: item.job.id,
    jobTitle: item.job.title,
    currentStage: item.currentStage,
    daysStuck: item.daysSinceUpdate,
  }));
}

// Tags API
export async function fetchTags(): Promise<Tag[]> {
  return api.get<Tag[]>('/tags');
}

export interface CreateTagInput {
  name: string;
  color: string;
}

export async function createTag(data: CreateTagInput): Promise<Tag> {
  return api.post<Tag>('/tags', data);
}

export interface UpdateTagInput {
  name?: string;
  color?: string;
}

export async function updateTag(id: string, data: UpdateTagInput): Promise<Tag> {
  return api.patch<Tag>(`/tags/${id}`, data);
}

export async function deleteTag(id: string): Promise<void> {
  return api.delete<void>(`/tags/${id}`);
}

// Candidate Tags API
export async function fetchCandidateTags(candidateId: string): Promise<CandidateTag[]> {
  return api.get<CandidateTag[]>(`/candidates/${candidateId}/tags`);
}

export async function assignTagToCandidate(candidateId: string, tagId: string): Promise<CandidateTag> {
  return api.post<CandidateTag>(`/candidates/${candidateId}/tags`, { tagId });
}

export async function removeTagFromCandidate(candidateId: string, tagId: string): Promise<void> {
  return api.delete<void>(`/candidates/${candidateId}/tags/${tagId}`);
}

// Files API
export type FileType = 'cv' | 'id_document' | 'certificate' | 'photo' | 'other';

export interface CandidateFile {
  id: string;
  candidateId: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  fileType: FileType;
  url: string;
  uploadedBy: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  createdAt: string;
}

// Transform backend file response to match frontend CandidateFile type
// Backend returns: fileName, uploadedAt, no url, no originalName
// Frontend expects: originalName, createdAt, url
function transformFileResponse(file: Record<string, unknown>): CandidateFile {
  return {
    ...file,
    originalName: (file.fileName as string) || (file.originalName as string) || 'Unknown',
    createdAt: (file.uploadedAt as string) || (file.createdAt as string) || new Date().toISOString(),
    url: (file.url as string) || `${API_BASE}/files/${file.id}`,
  } as CandidateFile;
}

export async function fetchCandidateFiles(candidateId: string): Promise<CandidateFile[]> {
  const files = await api.get<Record<string, unknown>[]>(`/candidates/${candidateId}/files`);
  return files.map(transformFileResponse);
}

export async function uploadCandidateFile(
  candidateId: string,
  file: File,
  fileType: FileType,
  onProgress?: (progress: number) => void,
): Promise<CandidateFile> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileType', fileType);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const response = JSON.parse(xhr.responseText);
        if (response.success) {
          resolve(transformFileResponse(response.data));
        } else {
          reject(new Error(response.error?.message || 'Upload failed'));
        }
      } else {
        try {
          const errResponse = JSON.parse(xhr.responseText);
          reject(new Error(errResponse.error?.message || `Upload failed (${xhr.status})`));
        } catch {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed - network error'));
    });

    xhr.open('POST', `${API_BASE}/candidates/${candidateId}/files?file_type=${encodeURIComponent(fileType)}`);

    const token = localStorage.getItem('crm_access_token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.send(formData);
  });
}

export async function deleteFile(fileId: string): Promise<void> {
  return api.delete<void>(`/files/${fileId}`);
}

// Company Files API
export type CompanyFileType = 'contract' | 'sla' | 'nda' | 'brief' | 'other';

export interface CompanyFile {
  id: string;
  companyId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  fileType: CompanyFileType;
  uploadedAt: string;
  uploadedBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

export async function fetchCompanyFiles(companyId: string): Promise<CompanyFile[]> {
  return api.get<CompanyFile[]>(`/companies/${companyId}/files`);
}

export async function uploadCompanyFile(
  companyId: string,
  file: File,
  fileType: CompanyFileType,
  onProgress?: (progress: number) => void,
): Promise<CompanyFile> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const response = JSON.parse(xhr.responseText);
        if (response.success) {
          resolve(response.data);
        } else {
          reject(new Error(response.error?.message || 'Upload failed'));
        }
      } else {
        try {
          const errResponse = JSON.parse(xhr.responseText);
          reject(new Error(errResponse.error?.message || `Upload failed (${xhr.status})`));
        } catch {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed - network error'));
    });

    xhr.open('POST', `${API_BASE}/companies/${companyId}/files?file_type=${encodeURIComponent(fileType)}`);

    const token = localStorage.getItem('crm_access_token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.send(formData);
  });
}

export async function deleteCompanyFile(fileId: string): Promise<void> {
  return api.delete<void>(`/files/${fileId}`);
}

// Notes API
export interface Note {
  id: string;
  content: string;
  isPinned: boolean;
  entityType: 'candidate' | 'job' | 'application' | 'company';
  entityId: string;
  mentions: string[];
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

export interface NotesFilters {
  page?: number;
  limit?: number;
}

export async function fetchNotes(
  entityType: 'candidate' | 'job' | 'application' | 'company',
  entityId: string,
  _filters: NotesFilters = {},
): Promise<Note[]> {
  // Backend returns "author" but frontend Note type uses "createdBy"
  // 'company' uses the generic /notes endpoint (no nested route); others use nested routes
  const endpoint = entityType === 'company'
    ? `/notes?entity_type=company&entity_id=${entityId}`
    : `/${entityType}s/${entityId}/notes`;
  const raw = await api.get<Array<Omit<Note, 'createdBy'> & { author: Note['createdBy'] }>>(endpoint);
  return raw.map(({ author, ...rest }) => ({ ...rest, createdBy: author }));
}

export interface CreateNoteInput {
  content: string;
  mentions?: string[];
}

export async function createNote(
  entityType: 'candidate' | 'job' | 'application' | 'company',
  entityId: string,
  data: CreateNoteInput,
): Promise<Note> {
  // 'company' uses the generic /notes endpoint (no nested route); others use nested routes
  let raw: Omit<Note, 'createdBy'> & { author: Note['createdBy'] };
  if (entityType === 'company') {
    raw = await api.post<Omit<Note, 'createdBy'> & { author: Note['createdBy'] }>('/notes', {
      entity_type: 'company',
      entity_id: entityId,
      ...data,
    });
  } else {
    raw = await api.post<Omit<Note, 'createdBy'> & { author: Note['createdBy'] }>(`/${entityType}s/${entityId}/notes`, data);
  }
  const { author, ...rest } = raw;
  return { ...rest, createdBy: author };
}

export interface UpdateNoteInput {
  content?: string;
  mentions?: string[];
}

export async function updateNote(noteId: string, data: UpdateNoteInput): Promise<Note> {
  const raw = await api.patch<Omit<Note, 'createdBy'> & { author: Note['createdBy'] }>(`/notes/${noteId}`, data);
  const { author, ...rest } = raw;
  return { ...rest, createdBy: author };
}

export async function toggleNotePin(noteId: string): Promise<Note> {
  const raw = await api.patch<Omit<Note, 'createdBy'> & { author: Note['createdBy'] }>(`/notes/${noteId}/pin`, {});
  const { author, ...rest } = raw;
  return { ...rest, createdBy: author };
}

export async function deleteNote(noteId: string): Promise<void> {
  return api.delete<void>(`/notes/${noteId}`);
}

// Users API (for mentions)
export interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string | null;
}

export async function fetchTeamMembers(): Promise<TeamMember[]> {
  return api.get<TeamMember[]>('/users');
}

// User Management API (Admin)
export interface UserListItem {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatarUrl: string | null;
  phone: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface UserFilters {
  search?: string;
  role?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export async function fetchUsers(
  filters: UserFilters = {},
): Promise<PaginatedResponse<UserListItem>> {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.role) params.append('role', filters.role);
  if (filters.isActive !== undefined) params.append('isActive', String(filters.isActive));
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  const queryString = params.toString();
  const endpoint = `/users${queryString ? `?${queryString}` : ''}`;

  return api.getPaginated<UserListItem>(endpoint);
}

export interface CreateUserInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: string;
  phone?: string;
}

export async function createUser(data: CreateUserInput): Promise<UserListItem> {
  return api.post<UserListItem>('/users', data);
}

export interface UpdateUserInput {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  phone?: string;
}

export async function updateUser(id: string, data: UpdateUserInput): Promise<UserListItem> {
  return api.patch<UserListItem>(`/users/${id}`, data);
}

export async function deactivateUser(id: string): Promise<UserListItem> {
  return api.patch<UserListItem>(`/users/${id}/deactivate`, {});
}

export async function reactivateUser(id: string): Promise<UserListItem> {
  return api.patch<UserListItem>(`/users/${id}/reactivate`, {});
}

// Profile API
export interface ProfileData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatarUrl: string | null;
  phone: string | null;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  lastLoginAt: string | null;
  createdAt: string;
}

export async function fetchProfile(): Promise<ProfileData> {
  return api.get<ProfileData>('/profile');
}

export interface UpdateProfileInput {
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export async function updateProfile(data: UpdateProfileInput): Promise<ProfileData> {
  return api.patch<ProfileData>('/profile', data);
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export async function changePassword(data: ChangePasswordInput): Promise<{ message: string }> {
  return api.patch<{ message: string }>('/profile/password', data);
}

// =============================================================================
// OPUS AI API
// =============================================================================

export interface OpusToolCall {
  name: string;
  duration: number;
  success: boolean;
}

export interface OpusUsage {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface OpusChatResponse {
  message: string;
  toolCalls: OpusToolCall[];
  conversationId: string;
  usage: OpusUsage;
}

export interface OpusConversation {
  id: string;
  title: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface OpusMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: OpusToolCall[];
  createdAt: string;
}

export interface OpusConversationDetail {
  conversation: {
    id: string;
    title: string;
    createdAt: string;
  };
  messages: OpusMessage[];
}

export interface OpusStreamEvent {
  type: 'text' | 'tool_start' | 'tool_end' | 'done' | 'error';
  content?: string;
  toolName?: string;
  toolInput?: unknown;
  toolResult?: unknown;
  usage?: OpusUsage;
  conversationId?: string;
  error?: string;
}

export async function sendOpusMessage(
  message: string,
  conversationId?: string
): Promise<OpusChatResponse> {
  return api.post<OpusChatResponse>('/opus-ai/chat', {
    message,
    conversationId,
  });
}

export async function* sendOpusMessageStream(
  message: string,
  conversationId?: string
): AsyncGenerator<OpusStreamEvent> {
  const token = localStorage.getItem('crm_access_token');

  const response = await fetch(`${API_BASE}/opus-ai/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ message, conversationId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(errorData.error?.message || 'Request failed');
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          try {
            const event = JSON.parse(data) as OpusStreamEvent;
            yield event;
          } catch {
            // Skip malformed JSON
          }
        }
      }
    }

    // Process any remaining data
    if (buffer.startsWith('data: ')) {
      const data = buffer.slice(6);
      try {
        const event = JSON.parse(data) as OpusStreamEvent;
        yield event;
      } catch {
        // Skip malformed JSON
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function fetchOpusConversations(limit = 20): Promise<OpusConversation[]> {
  return api.get<OpusConversation[]>(`/opus-ai/conversations?limit=${limit}`);
}

export async function fetchOpusConversation(conversationId: string): Promise<OpusConversationDetail> {
  return api.get<OpusConversationDetail>(`/opus-ai/conversations/${conversationId}`);
}

export async function deleteOpusConversation(conversationId: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/opus-ai/conversations/${conversationId}`);
}

export async function checkOpusAIStatus(): Promise<{ available: boolean; model: string | null }> {
  return api.get<{ available: boolean; model: string | null }>('/opus-ai/status');
}

// Opus AI Usage & Settings API
export interface OpusUsageSummary {
  totalConversations: number;
  totalMessages: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
  periodStart: string;
  periodEnd: string;
  byUser: Array<{
    userId: string;
    firstName: string;
    lastName: string;
    totalMessages: number;
    totalCostUsd: number;
  }>;
  byDay: Array<{
    date: string;
    messages: number;
    costUsd: number;
  }>;
}

export interface OpusUsageLog {
  id: string;
  userId: string;
  userName: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  toolCalls: string[];
  createdAt: string;
}

export interface OpusAiSettings {
  model: string;
  maxTokens: number;
  apiKeyConfigured: boolean;
  costLimitMonthly: number | null;
}

export async function fetchOpusUsageSummary(days = 30): Promise<OpusUsageSummary> {
  return api.get<OpusUsageSummary>(`/opus-ai/usage/summary?days=${days}`);
}

export async function fetchOpusUsageLogs(page = 1, limit = 20): Promise<{ data: OpusUsageLog[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
  return api.getPaginated<OpusUsageLog>(`/opus-ai/usage?page=${page}&limit=${limit}`);
}

export async function fetchOpusAiSettings(): Promise<OpusAiSettings> {
  return api.get<OpusAiSettings>('/opus-ai/settings');
}

export async function updateOpusAiSettings(data: {
  model?: string;
  maxTokens?: number;
  apiKey?: string;
  costLimitMonthly?: number | null;
}): Promise<OpusAiSettings> {
  return api.patch<OpusAiSettings>('/opus-ai/settings', data);
}

// Organization Billing Settings API
// Wire shape of organizations.settings.billing — the backend's
// InvoicePdfBillingSettings (invoice-pdf.service.ts) is authoritative.
export interface OrganizationBankDetails {
  bankName?: string | null;
  accountHolder?: string | null;
  accountNumber?: string | null;
  branchCode?: string | null;
  swift?: string | null;
}

export interface OrganizationBillingSettings {
  legalName?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  vatNumber?: string | null;
  registrationNumber?: string | null;
  taxLabel?: string | null;
  bankDetails?: OrganizationBankDetails | null;
}

export async function fetchOrganizationBillingSettings(): Promise<OrganizationBillingSettings> {
  return api.get<OrganizationBillingSettings>('/organization-settings/billing');
}

export async function updateOrganizationBillingSettings(
  data: OrganizationBillingSettings,
): Promise<OrganizationBillingSettings> {
  return api.put<OrganizationBillingSettings>('/organization-settings/billing', data);
}

// Global Search API
export interface GlobalSearchResult {
  candidates: Array<{
    id: string;
    firstName: string;
    lastName: string;
    currentJobTitle: string | null;
    currentLocation: string | null;
  }>;
  jobs: Array<{
    id: string;
    title: string;
    status: string;
    company: { name: string } | null;
  }>;
  companies: Array<{
    id: string;
    name: string;
    industry: string | null;
  }>;
}

export async function globalSearch(query: string): Promise<GlobalSearchResult> {
  return api.get<GlobalSearchResult>(`/search?q=${encodeURIComponent(query)}`);
}

// =============================================================================
// BULK OPERATIONS API
// =============================================================================

export async function bulkAddTags(candidateIds: string[], tagIds: string[]): Promise<{ affected: number }> {
  return api.post<{ affected: number }>('/bulk/tags', { candidateIds, tagIds });
}

export async function bulkAssignJob(candidateIds: string[], jobId: string, stage?: string): Promise<{ created: number }> {
  return api.post<{ created: number }>('/bulk/assign-job', { candidateIds, jobId, stage });
}

export async function bulkStageChange(applicationIds: string[], stage: string, notes?: string): Promise<{ updated: number }> {
  return api.post<{ updated: number }>('/bulk/stage-change', { applicationIds, stage, notes });
}

export async function bulkDeleteCandidates(candidateIds: string[]): Promise<{ deleted: number }> {
  return api.post<{ deleted: number }>('/bulk/candidates/delete', { candidateIds });
}

// =============================================================================
// EXPORT API
// =============================================================================

export async function exportData(
  entity: 'candidates' | 'jobs' | 'applications',
  format: 'csv' | 'xlsx',
  fields: string[],
  filters?: Record<string, string>,
): Promise<Blob> {
  // Route through the API client so auth, 401-refresh, and the friendly error
  // envelope (with reference code) are handled centrally instead of swallowing
  // the backend error behind a generic "Export failed".
  const { blob } = await api.requestBlob(`/export/${entity}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ format, fields, filters }),
  });
  return blob;
}

// =============================================================================
// NOTIFICATIONS API
// =============================================================================

export async function fetchNotifications(
  params: { page?: number; limit?: number; unreadOnly?: boolean } = {},
): Promise<PaginatedResponse<Notification>> {
  const qp = new URLSearchParams();
  if (params.page) qp.append('page', String(params.page));
  if (params.limit) qp.append('limit', String(params.limit));
  if (params.unreadOnly) qp.append('unread_only', 'true');
  return api.getPaginated<Notification>(`/notifications?${qp.toString()}`);
}

export async function fetchUnreadNotificationCount(): Promise<{ count: number }> {
  const response = await api.get<{ count?: number; unreadCount?: number }>('/notifications/unread-count');
  return { count: response.count ?? response.unreadCount ?? 0 };
}

export async function markNotificationRead(id: string): Promise<Notification> {
  return api.patch<Notification>(`/notifications/${id}/read`, {});
}

export async function markAllNotificationsRead(): Promise<{ updated: number }> {
  const response = await api.post<{ updated?: number; markedAsRead?: number }>('/notifications/mark-all-read');
  return { updated: response.updated ?? response.markedAsRead ?? 0 };
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  return api.get<NotificationPreferences>('/notifications/preferences');
}

export async function updateNotificationPreferences(
  data: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> {
  return api.put<NotificationPreferences>('/notifications/preferences', data);
}

// =============================================================================
// EMAIL API
// =============================================================================

export async function sendEmail(data: {
  toAddress: string;
  subject: string;
  body: string;
  candidateId?: string;
  jobId?: string;
  templateId?: string;
  cc?: string[];
  bcc?: string[];
}): Promise<Email> {
  return api.post<Email>('/emails/send', data);
}

export async function fetchEmails(
  params: { page?: number; limit?: number; candidateId?: string; jobId?: string } = {},
): Promise<PaginatedResponse<Email>> {
  const qp = new URLSearchParams();
  if (params.page) qp.append('page', String(params.page));
  if (params.limit) qp.append('limit', String(params.limit));
  if (params.candidateId) qp.append('candidateId', params.candidateId);
  if (params.jobId) qp.append('jobId', params.jobId);
  return api.getPaginated<Email>(`/emails?${qp.toString()}`);
}

export async function fetchEmailTemplates(): Promise<EmailTemplate[]> {
  return api.get<EmailTemplate[]>('/emails/templates');
}

export async function createEmailTemplate(data: {
  name: string;
  subject: string;
  body: string;
  variables?: string[];
  category?: string;
}): Promise<EmailTemplate> {
  return api.post<EmailTemplate>('/emails/templates', data);
}

export async function updateEmailTemplate(
  id: string,
  data: { name?: string; subject?: string; body?: string; variables?: string[]; category?: string },
): Promise<EmailTemplate> {
  return api.patch<EmailTemplate>(`/emails/templates/${id}`, data);
}

export async function deleteEmailTemplate(id: string): Promise<void> {
  return api.delete<void>(`/emails/templates/${id}`);
}

// =============================================================================
// REPORTS API
// =============================================================================

// Convert date-only string (YYYY-MM-DD) to ISO datetime for backend Zod validation
function toISOStart(date: string): string {
  return date.includes('T') ? date : `${date}T00:00:00.000Z`;
}
function toISOEnd(date: string): string {
  return date.includes('T') ? date : `${date}T23:59:59.999Z`;
}

function buildReportParams(startDate?: string, endDate?: string): URLSearchParams {
  const qp = new URLSearchParams();
  if (startDate) qp.append('startDate', toISOStart(startDate));
  if (endDate) qp.append('endDate', toISOEnd(endDate));
  return qp;
}

export async function fetchPipelineFunnel(startDate?: string, endDate?: string): Promise<PipelineFunnelData[]> {
  const qp = buildReportParams(startDate, endDate);
  return api.get<PipelineFunnelData[]>(`/reports/pipeline-funnel?${qp.toString()}`);
}

export async function fetchRecruiterPerformance(startDate?: string, endDate?: string): Promise<RecruiterPerformance[]> {
  const qp = buildReportParams(startDate, endDate);
  return api.get<RecruiterPerformance[]>(`/reports/recruiter-performance?${qp.toString()}`);
}

export async function fetchTimeToHire(startDate?: string, endDate?: string): Promise<TimeToHireData> {
  const qp = buildReportParams(startDate, endDate);
  return api.get<TimeToHireData>(`/reports/time-to-hire?${qp.toString()}`);
}

export async function fetchSourceAnalysis(startDate?: string, endDate?: string): Promise<SourceAnalysisData[]> {
  const qp = buildReportParams(startDate, endDate);
  return api.get<SourceAnalysisData[]>(`/reports/source-analysis?${qp.toString()}`);
}

// Finance reports -------------------------------------------------------------

export interface RevenueReportRow {
  period: string;
  currency: string;
  invoicedTotal: number;
  paidTotal: number;
  outstandingTotal: number;
  invoiceCount: number;
}

export interface AgedDebtorsReportRow {
  bucket: '0-30' | '31-60' | '61-90' | '90+';
  currency: string;
  count: number;
  totalOutstanding: number;
}

export interface CommissionsReportRow {
  userId: string;
  userName: string;
  currency: string;
  totalPending: number;
  totalPaid: number;
  placementCount: number;
}

export interface RevenueReportParams {
  period?: 'month';
  year?: number;
  currency?: string;
}

export async function fetchRevenueReport(params: RevenueReportParams = {}): Promise<RevenueReportRow[]> {
  const qp = new URLSearchParams();
  if (params.period) qp.append('period', params.period);
  if (params.year !== undefined) qp.append('year', String(params.year));
  if (params.currency) qp.append('currency', params.currency);
  const suffix = qp.toString();
  return api.get<RevenueReportRow[]>(`/reports/revenue${suffix ? `?${suffix}` : ''}`);
}

export async function fetchAgedDebtorsReport(): Promise<AgedDebtorsReportRow[]> {
  return api.get<AgedDebtorsReportRow[]>('/reports/aged-debtors');
}

export interface CommissionsReportParams {
  userId?: string;
  status?: 'pending' | 'paid';
}

export async function fetchCommissionsReport(params: CommissionsReportParams = {}): Promise<CommissionsReportRow[]> {
  const qp = new URLSearchParams();
  if (params.userId) qp.append('userId', params.userId);
  if (params.status) qp.append('status', params.status);
  const suffix = qp.toString();
  return api.get<CommissionsReportRow[]>(`/reports/commissions${suffix ? `?${suffix}` : ''}`);
}

// =============================================================================
// SUBMISSIONS API
// =============================================================================

export async function createSubmission(data: {
  companyId: string;
  jobId: string;
  candidateId: string;
  applicationId?: string;
  coverNote?: string;
}): Promise<CandidateSubmission> {
  return api.post<CandidateSubmission>('/submissions', data);
}

export async function fetchSubmissions(
  params: { companyId?: string; jobId?: string; status?: string; page?: number; limit?: number } = {},
): Promise<PaginatedResponse<CandidateSubmission>> {
  const qp = new URLSearchParams();
  if (params.companyId) qp.append('companyId', params.companyId);
  if (params.jobId) qp.append('jobId', params.jobId);
  if (params.status) qp.append('status', params.status);
  if (params.page) qp.append('page', String(params.page));
  if (params.limit) qp.append('limit', String(params.limit));
  return api.getPaginated<CandidateSubmission>(`/submissions?${qp.toString()}`);
}

export async function updateSubmissionStatus(id: string, status: string): Promise<CandidateSubmission> {
  return api.patch<CandidateSubmission>(`/submissions/${id}/status`, { status });
}

// =============================================================================
// PLACEMENTS API
// =============================================================================

export async function fetchPlacements(
  params: { page?: number; limit?: number; status?: string; companyId?: string } = {},
): Promise<PaginatedResponse<Placement>> {
  const qp = new URLSearchParams();
  if (params.page) qp.append('page', String(params.page));
  if (params.limit) qp.append('limit', String(params.limit));
  if (params.status) qp.append('status', params.status);
  if (params.companyId) qp.append('companyId', params.companyId);
  return api.getPaginated<Placement>(`/placements?${qp.toString()}`);
}

export async function fetchPlacement(id: string): Promise<Placement> {
  return api.get<Placement>(`/placements/${id}`);
}

export async function createPlacement(data: {
  candidateId: string;
  jobId: string;
  companyId: string;
  applicationId?: string;
  startDate?: string;
  salary?: number;
  salaryCurrency?: string;
  feeType?: string;
  feePercentage?: number;
  feeAmount?: number;
  notes?: string;
  commissions?: Array<{ userId: string; splitPercentage: number }>;
}): Promise<Placement> {
  return api.post<Placement>('/placements', data);
}

export async function updatePlacement(id: string, data: Record<string, unknown>): Promise<Placement> {
  return api.patch<Placement>(`/placements/${id}`, data);
}

// =============================================================================
// INVOICES API
// =============================================================================

export async function fetchInvoices(
  params: {
    page?: number;
    limit?: number;
    status?: string;
    companyId?: string;
    placementId?: string;
  } = {},
): Promise<PaginatedResponse<Invoice>> {
  const qp = new URLSearchParams();
  if (params.page) qp.append('page', String(params.page));
  if (params.limit) qp.append('limit', String(params.limit));
  if (params.status) qp.append('status', params.status);
  if (params.companyId) qp.append('companyId', params.companyId);
  if (params.placementId) qp.append('placementId', params.placementId);
  return api.getPaginated<Invoice>(`/invoices?${qp.toString()}`);
}

export async function fetchInvoicesForPlacement(
  placementId: string,
  params: { limit?: number } = {},
): Promise<PaginatedResponse<Invoice>> {
  return fetchInvoices({ placementId, limit: params.limit ?? 50 });
}

export async function fetchInvoicesForCompany(
  companyId: string,
  params: { limit?: number } = {},
): Promise<PaginatedResponse<Invoice>> {
  return fetchInvoices({ companyId, limit: params.limit ?? 50 });
}

export async function fetchInvoice(id: string): Promise<Invoice> {
  return api.get<Invoice>(`/invoices/${id}`);
}

export async function createInvoice(data: {
  companyId: string;
  placementId?: string;
  dueDate?: string;
  notes?: string;
  currency?: string;
  lineItems: Array<{ description: string; quantity: number; unitPrice: number }>;
  isCreditNote?: boolean;
  parentInvoiceId?: string;
}): Promise<Invoice> {
  return api.post<Invoice>('/invoices', data);
}

export async function updateInvoiceStatus(id: string, status: string, voidReason?: string): Promise<Invoice> {
  return api.patch<Invoice>(`/invoices/${id}/status`, { status, ...(voidReason ? { voidReason } : {}) });
}

export async function updateInvoice(id: string, data: {
  companyId?: string;
  placementId?: string | null;
  dueDate?: string | null;
  notes?: string | null;
  currency?: string;
  lineItems?: Array<{ description: string; quantity: number; unitPrice: number }>;
}): Promise<Invoice> {
  return api.patch<Invoice>(`/invoices/${id}`, data);
}

export async function deleteInvoice(id: string): Promise<void> {
  return api.delete(`/invoices/${id}`);
}

export async function checkOverdueInvoices(): Promise<{ markedOverdue: number }> {
  return api.post<{ markedOverdue: number }>('/invoices/check-overdue', {});
}

// --- Phase 2: Payments ---

export async function recordPayment(invoiceId: string, data: {
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  reference?: string | null;
  notes?: string | null;
}): Promise<Invoice> {
  return api.post<Invoice>(`/invoices/${invoiceId}/payments`, data);
}

export async function deletePayment(invoiceId: string, paymentId: string): Promise<Invoice> {
  return api.delete(`/invoices/${invoiceId}/payments/${paymentId}`) as Promise<Invoice>;
}

// --- Phase 3: PDF & Email ---

export function getInvoicePdfUrl(id: string): string {
  return `/api/invoices/${id}/pdf`;
}

export async function sendInvoice(id: string, data: {
  toAddress: string;
  ccAddresses?: string[];
  customMessage?: string;
}): Promise<void> {
  return api.post(`/invoices/${id}/send`, data) as unknown as Promise<void>;
}

// --- Phase 4: Recurring ---

export async function setRecurringSchedule(invoiceId: string, config: {
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'annually';
  nextDate: string;
  endDate?: string | null;
  autoSend: boolean;
}): Promise<Invoice> {
  return api.post<Invoice>(`/invoices/${invoiceId}/recurring`, config);
}

export async function removeRecurringSchedule(invoiceId: string): Promise<void> {
  return api.delete(`/invoices/${invoiceId}/recurring`) as unknown as Promise<void>;
}

// =============================================================================
// INTERVIEWS API
// =============================================================================

export async function fetchInterviews(
  params: { page?: number; limit?: number; candidateId?: string; jobId?: string; status?: string; upcoming?: boolean } = {},
): Promise<PaginatedResponse<Interview>> {
  const qp = new URLSearchParams();
  if (params.page) qp.append('page', String(params.page));
  if (params.limit) qp.append('limit', String(params.limit));
  if (params.candidateId) qp.append('candidateId', params.candidateId);
  if (params.jobId) qp.append('jobId', params.jobId);
  if (params.status) qp.append('status', params.status);
  if (params.upcoming) qp.append('upcoming', 'true');
  return api.getPaginated<Interview>(`/interviews?${qp.toString()}`);
}

export async function fetchInterview(id: string): Promise<Interview> {
  return api.get<Interview>(`/interviews/${id}`);
}

export async function createInterview(data: {
  candidateId: string;
  jobId: string;
  applicationId?: string;
  title: string;
  type: string;
  scheduledAt: string;
  durationMinutes?: number;
  location?: string;
  meetingLink?: string;
  notes?: string;
  attendees?: Array<{ userId?: string; externalName?: string; externalEmail?: string; role?: string }>;
}): Promise<Interview> {
  return api.post<Interview>('/interviews', data);
}

export async function updateInterview(id: string, data: Record<string, unknown>): Promise<Interview> {
  return api.patch<Interview>(`/interviews/${id}`, data);
}

export async function submitInterviewFeedback(id: string, data: { feedback: string; rating: number }): Promise<Interview> {
  return api.post<Interview>(`/interviews/${id}/feedback`, data);
}

export async function cancelInterview(id: string): Promise<void> {
  return api.delete<void>(`/interviews/${id}`);
}

// =============================================================================
// JOB BOARDS API
// =============================================================================

export async function fetchJobBoardAccounts(): Promise<JobBoardAccount[]> {
  return api.get<JobBoardAccount[]>('/job-boards/accounts');
}

export async function createJobBoardAccount(data: { provider: string; apiKey?: string }): Promise<JobBoardAccount> {
  return api.post<JobBoardAccount>('/job-boards/accounts', data);
}

export async function deleteJobBoardAccount(id: string): Promise<void> {
  return api.delete<void>(`/job-boards/accounts/${id}`);
}

export async function fetchJobBoardPostings(
  params: { jobId?: string; accountId?: string; status?: string } = {},
): Promise<JobBoardPosting[]> {
  const qp = new URLSearchParams();
  if (params.jobId) qp.append('jobId', params.jobId);
  if (params.accountId) qp.append('accountId', params.accountId);
  if (params.status) qp.append('status', params.status);
  return api.get<JobBoardPosting[]>(`/job-boards/postings?${qp.toString()}`);
}

export async function createJobBoardPosting(data: { jobId: string; accountId: string }): Promise<JobBoardPosting> {
  return api.post<JobBoardPosting>('/job-boards/postings', data);
}

export async function deleteJobBoardPosting(id: string): Promise<void> {
  return api.delete<void>(`/job-boards/postings/${id}`);
}

// =============================================================================
// CLIENT PORTAL ADMIN API
// =============================================================================

export interface PortalUser {
  id: string;
  companyId: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  company: { id: string; name: string };
  createdAt: string;
}

export async function fetchPortalUsers(
  params: { companyId?: string; page?: number; limit?: number } = {},
): Promise<PaginatedResponse<PortalUser>> {
  const qp = new URLSearchParams();
  if (params.companyId) qp.append('companyId', params.companyId);
  if (params.page) qp.append('page', String(params.page));
  if (params.limit) qp.append('limit', String(params.limit));
  return api.getPaginated<PortalUser>(`/portal-admin?${qp.toString()}`);
}

export async function createPortalUser(data: {
  companyId: string;
  contactId?: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}): Promise<PortalUser> {
  return api.post<PortalUser>('/portal-admin', data);
}

export async function updatePortalUser(id: string, data: Record<string, unknown>): Promise<PortalUser> {
  return api.patch<PortalUser>(`/portal-admin/${id}`, data);
}

export async function deactivatePortalUser(id: string): Promise<void> {
  return api.delete<void>(`/portal-admin/${id}`);
}

export async function resetPortalUserPassword(id: string, newPassword: string): Promise<void> {
  return api.post<void>(`/portal-admin/${id}/reset-password`, { newPassword });
}
